import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { AlimtalkFormCouponItem, AlimtalkSendFormValues } from '../../../types/alimtalk/form';
import { PickedCoupon } from './CouponPickerModal';

/** 폼 couponList 항목과 동형 — DTO 필드는 buildCreateAlimtalkSendDto에서 추출된다 */
export type CouponRow = AlimtalkFormCouponItem;

type UseCouponRowsOptions = {
  /** 신규 추가 쿠폰의 기본 발급 수량 — 만료 유형도 백엔드 @Min(1) 제약으로 1 (워커 미사용) */
  defaultIssuedCount: number;
  /** rows 변경 시 부가 처리 (예: 발급 유형의 alimtalkCouponId 정리) */
  onRowsChange?: (rows: CouponRow[]) => void;
};

export type UseCouponRowsReturn = {
  rows: CouponRow[];
  addCoupons: (picked: PickedCoupon[]) => void;
  removeCoupon: (couponId: number) => void;
  setIssuedCount: (couponId: number, value: number) => void;
  moveCoupon: (index: number, direction: -1 | 1) => void;
};

/**
 * 발급/만료 쿠폰 행(로컬 state)을 관리하고 form의 couponList와 동기화하는 훅.
 * CouponIssueSection / CouponExpireSection 공통 로직.
 */
export const useCouponRows = (options: UseCouponRowsOptions): UseCouponRowsReturn => {
  const { defaultIssuedCount, onRowsChange } = options;
  const { setValue } = useFormContext<AlimtalkSendFormValues>();
  const [rows, setRows] = useState<CouponRow[]>([]);

  const sync = (next: CouponRow[]): void => {
    setRows(next);
    // 파생 필드 포함 전체 저장 — 검증(쿠폰 기간 일치)이 폼 값만으로 가능하도록. DTO 추출은 buildCreateAlimtalkSendDto 담당.
    setValue('couponList', next);
    onRowsChange?.(next);
  };

  const addCoupons = (picked: PickedCoupon[]): void => {
    const merged = [...rows];
    picked.forEach((coupon) => {
      if (!merged.some((row) => row.couponId === coupon.couponId)) {
        merged.push({
          couponId: coupon.couponId,
          name: coupon.name,
          issuedCount: defaultIssuedCount,
          periodType: coupon.periodType,
          startDate: coupon.startDate,
          endDate: coupon.endDate,
          dayPlus: coupon.dayPlus,
        });
      }
    });
    sync(merged);
  };

  const removeCoupon = (couponId: number): void => {
    sync(rows.filter((row) => row.couponId !== couponId));
  };

  const setIssuedCount = (couponId: number, value: number): void => {
    sync(rows.map((row) => (row.couponId === couponId ? { ...row, issuedCount: value } : row)));
  };

  const moveCoupon = (index: number, direction: -1 | 1): void => {
    const target = index + direction;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    [next[index], next[target]] = [next[target], next[index]];
    sync(next);
  };

  return { rows, addCoupons, removeCoupon, setIssuedCount, moveCoupon };
};
